import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"
import { EnumDayOfWeek } from "./enums/enumDayOfWeek.generated";

// Model Objects

export class CollectionConfiguration extends ModelObject<CollectionConfiguration> {

    /**
    * Array of days of week on which orders may be collected. Will always read in order Monday to Sunday.
    */
    public collectionDays: (EnumDayOfWeek)[] = [];
    /**
    * If true, then orders placed before CollectionCutOffTime can be collected on the same day, else next available collection day.
    */
    public canCollectSameDay: boolean = false;
    private _collectionCutOffTime: LocalTime | null = null;
    /**
    * Cut off time for placing an order for collection. Orders placed after this will be delayed by an additional day before they are ready.
    */
    public get collectionCutOffTime(): LocalTime | null { return this._collectionCutOffTime }
    public set collectionCutOffTime(value: LocalTime | null) {
      this._collectionCutOffTime = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.collectionDays = jsonSerializedObject.collectionDays.map((v: any) => EnumDayOfWeek.getByValue(v));
          this.canCollectSameDay = jsonSerializedObject.canCollectSameDay;
          this.collectionCutOffTime = jsonSerializedObject.collectionCutOffTime == null ? null : LocalTime.parse(jsonSerializedObject.collectionCutOffTime);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.collectionDays = this.collectionDays.map(v => v.value);
        toRet.canCollectSameDay = this.canCollectSameDay;
        toRet.collectionCutOffTime = this.collectionCutOffTime == null ? null : this.collectionCutOffTime.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
    ];
}


export class DistributionConfiguration extends ModelObject<DistributionConfiguration> {

    public version: number = 0;
    /**
    * Default stop minutes for new Outlets / Visits.
    * Not Negative
    */
    public defaultStopMinutes: number = 0;
    /**
    * If true, then the distribution app shows an email invoice option for delivered orders.
    */
    public canEmailInvoicesFromDistributionApp: boolean = false;
    public collection: CollectionConfiguration = new CollectionConfiguration();

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.defaultStopMinutes = jsonSerializedObject.defaultStopMinutes;
          this.canEmailInvoicesFromDistributionApp = jsonSerializedObject.canEmailInvoicesFromDistributionApp;
          this.collection = new CollectionConfiguration(jsonSerializedObject.collection);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.defaultStopMinutes = this.defaultStopMinutes;
        toRet.canEmailInvoicesFromDistributionApp = this.canEmailInvoicesFromDistributionApp;
        toRet.collection = this.collection.toJsonSerializedObject();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "defaultStopMinutes", rule: (v: any) => v == null || v >= 0 || "DefaultStopMinutes is not greater than minimum allowed value: 0"},
            {field: "defaultStopMinutes", rule: (v: any) => v == null || v <= 2147483647 || "DefaultStopMinutes is above maximum allowed value: 2147483647"},
            {field: "collection", rule: (v: any) => !!v || "collection is required"},
            {field: "collection", rule: (v: any) => v.validate()},
    ];
}


export class DistributionConfigurationService extends ServiceBase {

  public async getDistributionConfiguration(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<DistributionConfiguration> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => new DistributionConfiguration(response.distributionConfiguration);;
    return this.makeJsonRequest("distributionConfiguration/v1/getDistributionConfiguration", req, false, extractor, opts);
  };

  public async saveDistributionConfiguration(configuration: DistributionConfiguration, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    configuration:configuration.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("distributionConfiguration/v1/saveDistributionConfiguration", req, true, extractor, opts);
  };
}

export const distributionConfigurationService = new DistributionConfigurationService();